import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  // Premium design system colors – inspired by Phoenix/Falcon/Mantis
  const theme = {
    isDark,
    toggleTheme,
    colors: isDark ? {
      // ── Dark Theme ──────────────────────────
      background: '#0B1120',
      backgroundSecondary: '#0F172A',
      surface: '#131C31',
      surfaceHover: '#1A2542',
      surfaceElevated: '#182034',
      
      primary: '#3B82F6',
      primaryLight: '#60A5FA',
      primaryDark: '#2563EB',
      primaryGhost: 'rgba(59, 130, 246, 0.08)',
      primaryBorder: 'rgba(59, 130, 246, 0.25)',

      secondary: '#8B5CF6',
      secondaryLight: '#A78BFA',
      
      accent: '#06B6D4',
      accentLight: '#22D3EE',
      
      text: '#F1F5F9',
      textSecondary: '#94A3B8',
      textMuted: '#64748B',
      textInverse: '#0F172A',
      
      border: 'rgba(148, 163, 184, 0.12)',
      borderHover: 'rgba(148, 163, 184, 0.2)',
      borderActive: 'rgba(59, 130, 246, 0.5)',
      
      success: '#22C55E',
      successLight: '#4ADE80',
      successGhost: 'rgba(34, 197, 94, 0.1)',
      
      warning: '#F59E0B',
      warningLight: '#FBBF24',
      warningGhost: 'rgba(245, 158, 11, 0.1)',
      
      error: '#EF4444',
      errorLight: '#F87171',
      errorGhost: 'rgba(239, 68, 68, 0.1)',
      
      info: '#06B6D4',
      infoLight: '#22D3EE',
      infoGhost: 'rgba(6, 182, 212, 0.1)',
      
      card: '#131C31',
      cardHover: '#1A2542',
      
      sidebar: '#0D1526',
      sidebarHover: 'rgba(59, 130, 246, 0.08)',
      sidebarActive: 'rgba(59, 130, 246, 0.15)',
      sidebarBorder: 'rgba(148, 163, 184, 0.08)',
      
      headerBg: 'rgba(11, 17, 32, 0.8)',
      
      shadow: '0 4px 24px -4px rgba(0, 0, 0, 0.3)',
      shadowLg: '0 12px 40px -8px rgba(0, 0, 0, 0.4)',
      
      chartGrid: 'rgba(148, 163, 184, 0.1)',
    } : {
      // ── Light Theme ─────────────────────────
      background: '#F8FAFC',
      backgroundSecondary: '#F1F5F9',
      surface: '#FFFFFF',
      surfaceHover: '#F8FAFC',
      surfaceElevated: '#FFFFFF',
      
      primary: '#3B82F6',
      primaryLight: '#60A5FA',
      primaryDark: '#2563EB',
      primaryGhost: 'rgba(59, 130, 246, 0.06)',
      primaryBorder: 'rgba(59, 130, 246, 0.2)',

      secondary: '#8B5CF6',
      secondaryLight: '#A78BFA',
      
      accent: '#06B6D4',
      accentLight: '#22D3EE',
      
      text: '#0F172A',
      textSecondary: '#64748B',
      textMuted: '#94A3B8',
      textInverse: '#FFFFFF',
      
      border: 'rgba(148, 163, 184, 0.2)',
      borderHover: 'rgba(148, 163, 184, 0.35)',
      borderActive: 'rgba(59, 130, 246, 0.4)',
      
      success: '#16A34A',
      successLight: '#22C55E',
      successGhost: 'rgba(22, 163, 74, 0.08)',
      
      warning: '#D97706',
      warningLight: '#F59E0B',
      warningGhost: 'rgba(217, 119, 6, 0.08)',
      
      error: '#DC2626',
      errorLight: '#EF4444',
      errorGhost: 'rgba(220, 38, 38, 0.08)',
      
      info: '#0891B2',
      infoLight: '#06B6D4',
      infoGhost: 'rgba(8, 145, 178, 0.08)',
      
      card: '#FFFFFF',
      cardHover: '#F8FAFC',
      
      sidebar: '#FFFFFF',
      sidebarHover: 'rgba(59, 130, 246, 0.06)',
      sidebarActive: 'rgba(59, 130, 246, 0.1)',
      sidebarBorder: 'rgba(148, 163, 184, 0.15)',
      
      headerBg: 'rgba(255, 255, 255, 0.85)',
      
      shadow: '0 4px 24px -4px rgba(0, 0, 0, 0.06)',
      shadowLg: '0 12px 40px -8px rgba(0, 0, 0, 0.1)',
      
      chartGrid: 'rgba(148, 163, 184, 0.15)',
    }
  };

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};